import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { useSelector, useDispatch } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/pro-solid-svg-icons'

import ConfirmationModal from 'components/ui/modals/confirmation/ConfirmationModal'
import * as fromCourseActions from 'store/actions/courses.action'
import { IMainState } from 'store/states'
import { CourseStateType } from 'enum'
import { DC3_CERTIFICATE_TEMPLATES_PATH } from 'config/dc3'
import { PublishCourseButton } from './LessonsFormFooter/styled'
import { PublishNormativeWithoutDc3Modal } from './PublishNormativeWithoutDc3Modal'

const invitationModalPrefix =
  'administration.courses.new.form.invitationNotification.confirmationModal'

type PublishRedirectMode = 'coursesList' | 'certificateTemplates'

interface PublishButtonProps {
  draggable: boolean
  disabled?: boolean
  shouldConfirmNormativePublish?: boolean
}

const PublishButton: React.FC<PublishButtonProps> = ({
  draggable,
  disabled = false,
  shouldConfirmNormativePublish = false,
}) => {
  const { t } = useTranslation()
  const { courseId } = useParams<Record<'courseId', string>>()
  const dispatch = useDispatch()
  const { course } = useSelector(({ courses }: IMainState) => courses)

  const [buttonLoading, setButtonLoading] = useState(false)
  const [showPublishConfirmation, setShowPublishConfirmation] = useState(false)
  const [showNormativePublishModal, setShowNormativePublishModal] =
    useState(false)
  const [pendingRedirectMode, setPendingRedirectMode] =
    useState<PublishRedirectMode>('coursesList')

  const courseInvitation = course.invitationNotification
  const invitationWillSent = courseInvitation
    ? !courseInvitation.sent &&
      (courseInvitation.active || !!courseInvitation.pushActive)
    : false

  const dispatchPublish = (redirectMode: PublishRedirectMode) => {
    setButtonLoading(true)
    setShowNormativePublishModal(false)
    setShowPublishConfirmation(false)

    dispatch({
      type: fromCourseActions.PUBLISH_COURSE,
      payload: {
        course: { state: CourseStateType.Published },
        courseId,
        onlySave: true,
        redirectPath:
          redirectMode === 'certificateTemplates'
            ? DC3_CERTIFICATE_TEMPLATES_PATH
            : undefined,
        dc3AssociatePublish: redirectMode === 'certificateTemplates',
        courseName: course.name,
      },
    })
  }

  const runPublishFlow = (redirectMode: PublishRedirectMode) => {
    if (invitationWillSent) {
      setPendingRedirectMode(redirectMode)
      setShowPublishConfirmation(true)
      return
    }

    dispatchPublish(redirectMode)
  }

  const onPublishClick = () => {
    if (shouldConfirmNormativePublish) {
      setShowNormativePublishModal(true)
      return
    }

    runPublishFlow('coursesList')
  }

  const isDisabled = buttonLoading || draggable || disabled

  return (
    <>
      <PublishCourseButton
        type="button"
        disabled={isDisabled}
        onClick={() => !isDisabled && onPublishClick()}
      >
        {buttonLoading ? (
          <FontAwesomeIcon icon={faSpinner} spin aria-hidden />
        ) : null}
        {t('administration.lessons.buttons.publishCourse')}
      </PublishCourseButton>

      {showNormativePublishModal && (
        <PublishNormativeWithoutDc3Modal
          isOpen={showNormativePublishModal}
          processing={buttonLoading}
          onClose={() => setShowNormativePublishModal(false)}
          onPublishAndAssociate={() => runPublishFlow('certificateTemplates')}
        />
      )}

      {invitationWillSent && showPublishConfirmation && (
        <ConfirmationModal
          show
          title={t(`${invitationModalPrefix}.header`)}
          acceptButtonText={t(`${invitationModalPrefix}.continue`)}
          text={t(`${invitationModalPrefix}.text`)}
          onAccept={() => dispatchPublish(pendingRedirectMode)}
          onClose={() => setShowPublishConfirmation(false)}
        />
      )}
    </>
  )
}

export default PublishButton
